import * as React from 'react';

import { clsx } from '@freelancelabs/utils';

export type RegionStateTone = 'empty' | 'error';

export interface RegionStateProps {
    /**
     * What the state means, in one sentence. Never « no data »: an empty region is
     * often the ordinary case, and it has to say which case it is.
     */
    title: string;
    /** Why the region is in this state, and what the reader can do about it. */
    description?: React.ReactNode;
    /** An action that resolves the state: clear the filters, retry the query. */
    children?: React.ReactNode;
    /**
     * `error` is not decoration. An error rendered as an absence sends the reader
     * away convinced there is nothing to see, which is worse than the error.
     */
    tone?: RegionStateTone;
}

/**
 * The non-nominal states of a REGION of the screen: nothing to show, nothing
 * matching, or a query that failed.
 *
 * **IT WAS CALLED `RegionState`, and the rename is the whole point of this lot.** The
 * old name was true the day it was written: it served the three states of a LIST. It
 * now serves the portal home, the two MFA guards, the financial-chain card and the
 * activities showcase, none of which is a list. The repository had noted the
 * threshold at three uses outside a list; it stands at double that, and a name that
 * lies costs more at every borrowing.
 *
 * **It was never rendered INSIDE a table**, and that is what made the promotion
 * mechanical rather than a rewrite: the scroll container is as wide as the widest
 * row, so a message centred in it lands off-screen. It has always been placed
 * beside.
 */
const RegionState = ({ title, description, children, tone = 'empty' }: RegionStateProps) => (
    <div
        className={clsx('fl-region-state', `fl-region-state-${tone}`)}
        // role="alert" already implies an assertive live region; declaring both
        // makes the two announcements fight each other.
        {...(tone === 'error' ? { role: 'alert' } : { 'aria-live': 'polite' as const })}
    >
        <p className="fl-region-state-title">{title}</p>
        {description && <p className="fl-region-state-description">{description}</p>}
        {children && <div className="fl-region-state-action">{children}</div>}
    </div>
);

export default RegionState;
